import { useCallback, useEffect, useRef } from 'react';

/**
 * Cropper reports ready before the browser has painted its canvas. Delay the handoff to the editor by one
 * complete paint opportunity so the existing view image and the editor never disappear in the same frame.
 */
export const useImageModalEditorVisualCommit = (
    isEditorSurfaceReady: () => boolean,
    onVisualReadyChange?: (ready: boolean) => void,
) => {
    const animationFrameRef = useRef<number | null>(null);

    const cancelPendingVisualCommit = useCallback(() => {
        if (animationFrameRef.current === null) return;

        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
    }, []);

    const handleLoaderReadyChange = useCallback(
        (ready: boolean) => {
            cancelPendingVisualCommit();

            if (!ready) {
                onVisualReadyChange?.(false);
                return;
            }

            const commitWhenSurfaceIsReady = () => {
                animationFrameRef.current = requestAnimationFrame(() => {
                    if (!isEditorSurfaceReady()) {
                        commitWhenSurfaceIsReady();
                        return;
                    }

                    animationFrameRef.current = null;
                    onVisualReadyChange?.(true);
                });
            };

            commitWhenSurfaceIsReady();
        },
        [cancelPendingVisualCommit, isEditorSurfaceReady, onVisualReadyChange],
    );

    useEffect(() => cancelPendingVisualCommit, [cancelPendingVisualCommit]);

    return handleLoaderReadyChange;
};
